
/**
 * mediationFillResult.js — the GMA SDK's mediation fill-result report, joined to its slot.
 * Twin: proxy-server/lifecycle/mediationFillResult.js (pinned by mediationFillResultParity.test.js).
 *
 * After a mediated / SDK-bidding win the Google SDK fires one of the GAM response's
 * `fill_urls`: /pagead/interaction/?…&label=drx_mediation_request_fill_result
 * &mediation_fill_status=0.<0 filled|1 failed|2 timed out>.<ttr ms>.0[.<adapter error code>]&ttr=…&sigh=…
 * It is the only on-wire proof that the winning adapter actually loaded its ad: a Meta
 * win that got Meta "1001 No fill" reads 0.1.568.0.1001 (iOS HAR, 2026-10-18); three AdX
 * wins that never loaded read 0.2.60006.0 (iOS HAR, 2026-10-20). Not an impression.
 * Joined by `sigh`, which the GAM response embeds (validData.expanded.sighAll).
 *
 * Pure. Never throws.
 */

const FILL_RESULT_LABEL = 'drx_mediation_request_fill_result';

function qp(url, name) {
  const m = String(url || '').match(new RegExp(`[?&]${name}=([^&#]*)`));
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

// Meta Audience Network error codes, from Meta's "Ad Request Error Codes" page
// (developers.facebook.com/documentation/audience-network/setting-up/test/checklist-errors).
// Only applied when the winning adapter is Meta: the adapter error code is the buyer's own.
const META_ERRORS = {
  1000: 'Network Error: the SDK could not reach Meta',
  1001: 'No Fill',
  1002: 'Ad Load Too Frequently',
  1011: 'Display Format Mismatch',
  1203: 'Not an App Admin, Developer or Tester',
  2000: 'Server Error',
  2001: 'Internal Error',
};

/** Meta's name for an adapter error code, or null when the buyer is not Meta / code unknown. */
function adapterErrorName(code, who) {
  if (code == null || !/meta|facebook/i.test(String(who || ''))) return null;
  return META_ERRORS[code] || null;
}

function isFillResultRow(row) {
  return !!row && typeof row.url === 'string' && row.url.includes(FILL_RESULT_LABEL);
}

/** { failed, timedOut, ttrMs, errorCode, sigh, status } from a fill-result URL, or null. A timeout counts as failed. */
function parseFillResult(url) {
  try {
    if (!String(url || '').includes(FILL_RESULT_LABEL)) return null;
    const status = qp(url, 'mediation_fill_status');
    if (!status) return null;
    const parts = status.split('.');
    if (parts[1] !== '0' && parts[1] !== '1' && parts[1] !== '2') return null;
    const ttr = Number(parts[2] != null ? parts[2] : qp(url, 'ttr'));
    return {
      failed: parts[1] !== '0',
      timedOut: parts[1] === '2',
      ttrMs: Number.isFinite(ttr) ? ttr : null,
      errorCode: parts[4] != null && parts[4] !== '' ? parts[4] : null,
      sigh: qp(url, 'sigh'),
      status,
    };
  } catch { return null; }
}

/**
 * The fill-result report for one GAM row: a fill-result row whose sigh is in the GAM
 * response's sighAll. Exact join — no time window. Returns null when the response
 * was not kept (no sighAll) or no report fired.
 */
function fillResultForGam(gamRow, fillRows) {
  try {
    const ex = (gamRow && gamRow.validData && gamRow.validData.expanded) || {};
    const sighs = Array.isArray(ex.sighAll) ? ex.sighAll : (ex.sigh ? [ex.sigh] : []);
    if (!sighs.length) return null;
    for (const r of fillRows || []) {
      const p = parseFillResult(r && r.url);
      if (p && p.sigh && sighs.includes(p.sigh)) {
        return { ...p, rowKey: r._k != null ? r._k : (r.id != null ? r.id : null) };
      }
    }
    return null;
  } catch { return null; }
}

/**
 * Slot issue for a failed load, or null. `who` = the winning adapter's label.
 * `opts.noFallback`: GAM's response listed only the winner (ad_networks length 1), so
 * nothing could load after it.
 */
function fillResultIssue(fill, who, opts = {}) {
  if (!fill || !fill.failed) return null;
  const name = who || 'The winning mediation buyer';
  const errName = adapterErrorName(fill.errorCode, who);
  const detail = [
    fill.errorCode ? `error ${fill.errorCode}${errName ? ` "${errName}"` : ''}` : null,
    fill.ttrMs != null ? `after ${fill.ttrMs} ms` : null,
  ].filter(Boolean).join(', ');
  const what = fill.timedOut ? 'its ad timed out before loading' : 'its SDK failed to load the ad';
  return {
    level: 'fail',
    msg: `${name} won GAM's auction but ${what}${detail ? ` (${detail})` : ''}: the slot stayed empty${opts && opts.noFallback ? ', because GAM sent no fallback network after the winner' : ''}`,
    phase: 'winner',
    code: 'mediation_load_failed',
    rowKey: fill.rowKey,
  };
}

export { FILL_RESULT_LABEL, isFillResultRow, parseFillResult, fillResultForGam, fillResultIssue, adapterErrorName };
